import axios, {AxiosInstance, AxiosRequestConfig, AxiosError, CancelTokenSource, CancelToken, AxiosResponseTransformer} from 'axios';
import {camelCase, isArray, isObject, mapKeys, mapValues} from 'lodash';
import {getApiUrl} from '@/api/common-utils';
import Router from 'next/router';
import {flushTokenFromCookies, getTokenFromCookies, setTokenIntoCookies, getHasRegisteredFromStorage} from './common-utils';
import {notification} from '@/utils/notification';
import {createSpanStatusInterceptors} from '@/utils/otel/api-tracer';

// Extend AxiosError type to include our custom property
declare module 'axios' {
  interface AxiosError {
    isNotificationHandled?: boolean;
  }
}

const tokenSources: {
  [key: string]: CancelTokenSource;
} = {};

export type ApiErrorType = {
  statusCode: number;
  errorCode: string;
  message: string;
};

function refreshAuthToken() {
  const token = getTokenFromCookies();
  const response = axios.post(`${getApiUrl(BaseApi.LINKGRAPH_ENDPOINT, '/api/token/refresh/')}`, {
    token,
  });
  return response;
}

function pushToLoginOrRegister() {
  flushTokenFromCookies();
  const hasRegistered = getHasRegisteredFromStorage();
  if (hasRegistered === 'YES') {
    Router.push('/login');
  } else {
    Router.push('/register');
  }
}

async function onError(error: AxiosError, axiosInstance: AxiosInstance, errorList?: ApiErrorType[]) {
  let throwError = true;
  let notificationShown = false; // Track if we've already shown a notification

  // return valid/empty response and exit
  if (axios.isCancel(error)) {
    return Promise.resolve({data: {isCancel: true}});
  }

  // Common error handling
  const {response} = error;
  if (!response) {
    notification.error('Error.', 'Something went wrong.', false, 'Refresh page', 0);
  } else if (response.status === 401) {
    // Interceptor that retries 401s by refreshing
    // tokens automatically only if token exists.
    try {
      const _response = await refreshAuthToken();
      axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${_response.data.token}`;
      setTokenIntoCookies(_response.data.token);
      return axiosInstance(error.config);
    } catch (err) {
      notification.error('Session expired', 'Your session has expired. Please login again.', false, 'Refresh page', 0);
      pushToLoginOrRegister();
    }
  } else if (response.status === 429 && response?.data && response?.data['code'] === 'all_seats_occupied') {
    notification.error('Seat Quota Exhausted', response.data['detail'], false, 'Refresh page', 0);
    pushToLoginOrRegister();
  } else if (response.status === 429) {
    if (response?.config?.url !== apiUrls.rankTracker && response?.config?.url !== apiUrls.bulkArticleGenerateCA && !response?.config?.url.includes('cloud-stack-contents')) {
      const isGHLAccount = JSON.parse(localStorage.getItem('isGHLAccount'));
      const notificationMessage = isGHLAccount ? "You've reached the limit to optimize this page. Need more? Reach out to our support team for help!" : "You're currently out of quota. Please upgrade your plan to Optimize this Page.";
      notification.error('Quota Exhausted', notificationMessage, false, 'Refresh page', 0);
    }
  } else {
    // Any other error.
    const {status, data} = response;

    // Handle specific HTTP errors
    if (status >= 500 && !notificationShown) {
      // For all 5xx errors, show a notification and don't throw
      notification.error('Server Error', 'Something went wrong on the server.', false, 'Refresh page', status, error.config?.url);
      error.isNotificationHandled = true;
      notificationShown = true;
      throwError = false; // Don't throw the error after handling it
    } else {
      notificationShown = true;
      throwError = true;
    }

    if (errorList && errorList.length && !notificationShown) {
      const errorItem = errorList.filter(err => err.statusCode == status && err.errorCode == data['code']).pop();
      if (errorItem) {
        throwError = false;
        notification.error('Error', errorItem.message, false, 'Refresh page', 0);
      }
    }
  }

  if (throwError) {
    throw error;
  } else {
    return Promise.resolve({
      data: {
        error: true,
        handled: true,
        status: response?.status || 0,
        message: typeof error === 'object' && error !== null ? (error as any).message || 'Unknown error' : 'Error handled',
      },
    });
  }
}


function keysToCamelCase<T>(obj: T): T {
  if (isArray(obj)) {
    // @ts-ignore
    return obj.map(keysToCamelCase);
  }
  if (!isObject(obj)) {
    return obj;
  }
  const fixedKeys = mapKeys(obj, (value, key) => camelCase(key));
  // @ts-ignore
  return mapValues(fixedKeys, keysToCamelCase);
}

// tslint:disable-next-line:no-any
function transformResponse(data: any): any {
  return keysToCamelCase(data);
}

function getDefaultTransformResponse(): AxiosResponseTransformer[] {
  const axiosDefault = axios.defaults.transformResponse;
  if (axiosDefault == null) {
    return [transformResponse];
  }
  if (isArray(axiosDefault)) {
    // @ts-ignore
    return axiosDefault?.concat(transformResponse);
  }
  // @ts-ignore
  return [axiosDefault, transformResponse];
}

const CAMEL_CASE_DEFAULT_CONFIG: AxiosRequestConfig = {
  transformResponse: getDefaultTransformResponse(),
};

async function onCancelRequest(config: AxiosRequestConfig) {
  const key = config.url;
  if (config.cancelToken && key) {
    let tokenSource = tokenSources[key];
    if (tokenSource && tokenSource.cancel) {
      tokenSource.cancel('Operation canceled due to new request');
    }
    tokenSource = axios.CancelToken.source();
    config.cancelToken = tokenSource.token;
    tokenSources[key] = tokenSource;
  }

  // Add trace header to every request
  if (!config.headers) {
    config.headers = {};
  }

  return config;
}

export class BaseApi {
  public static readonly LINKGRAPH_ENDPOINT: string = 'lg';
  public static readonly GSC_ENDPOINT: string = 'gsc';
  public static readonly BACKLINK_ENDPOINT: string = 'bl';
  public static readonly KEYWORD_ENDPOINT: string = 'kw';
  public static readonly CA_ENDPOINT: string = 'ca';
  public static readonly SA_ENDPOINT: string = 'sa';
  public static readonly OTTO_PPC_ENDPOINT: string = 'otto-ppc';
  public static readonly APP_HEALTH_CHECK_ENDPOINT:string = 'status';

  axios: AxiosInstance;
  axiosWithoutAuth: AxiosInstance;
  axiosWithoutCamelCase: AxiosInstance;
  cancelToken: CancelToken;

  constructor(baseURL?: string, errorList?: ApiErrorType[]) {
    this.axios = axios.create({...CAMEL_CASE_DEFAULT_CONFIG, baseURL});

    // Add OTEL interceptors that set status codes properly
    const [otelSuccess] = createSpanStatusInterceptors();
    // Keep existing error handling
    this.axios.interceptors.response.use(
      res => otelSuccess(res),
      err => {
        return onError(err, this.axios, errorList);
      },
    );
    this.axios.interceptors.request.use(
      onCancelRequest,
    );
    this.axiosWithoutAuth = axios.create({...CAMEL_CASE_DEFAULT_CONFIG, baseURL});
    this.axiosWithoutAuth.interceptors.request.use(config=> onCancelRequest(config));

    // Add OTEL interceptors to the other axios instances too
    this.axiosWithoutAuth.interceptors.response.use(
      res => otelSuccess(res),
      err => {
        return onError(err, this.axios, errorList);
      },
    );

    this.axiosWithoutCamelCase = axios.create({baseURL});
    this.axiosWithoutCamelCase.interceptors.request.use(config=> onCancelRequest(config));

    // Add OTEL interceptors
    this.axiosWithoutCamelCase.interceptors.response.use(
      res => otelSuccess(res),
      err => {
        return onError(err, this.axios, errorList);
      },
    );
    this.axiosWithoutCamelCase.interceptors.request.use(
      onCancelRequest,
    );
    this.cancelToken = axios.CancelToken.source().token;
  }
}

const apiUrls = {
  rankTracker: `${getApiUrl(BaseApi.KEYWORD_ENDPOINT, '/api')}/v1/rank-tracker/`,
  bulkArticleGenerateCA: `content-fragments/full-seo-article-writer-bulk/`,
};

